import type { ChildAppConfig } from '@shared/app-config'
import type { ThemeMode } from '@shared/app-config/types'
import type { ToastMessageOptions } from 'primevue/toast'
import type { LayoutSnapshot } from '@/proxy/shared/layout'
import type { LinkClassification } from '@/proxy/shared/link-classifier'
import type { LimitedConfirmationOptions, ProxyApiInstance } from '@/proxy/shared/types'
import { getAppConfig } from '@shared/app-config/lib'
import { IFRAME_MESSAGE_TYPE, IFrameMessageType, sendIframeMessage } from '@shared/iframe-messages'
import { uuid } from '@shared/util/uuid'
import { createBridgeAPI } from '@/proxy/shared/bridge'
import { createLayoutAPI } from '@/proxy/shared/layout'
import { classifyLink } from '@/proxy/shared/link-classifier'

export function createHostAPI(appConfig: ChildAppConfig): ProxyApiInstance['host'] {
  const bridge = createBridgeAPI()
  return {
    startChat: (start_token: string, options?: { sidebar?: boolean }) => {
      sendIframeMessage(IFrameMessageType.CmdStartChat, { start_token, sidebar: options?.sidebar || false })
    },
    openSession: (sessionUUID: string, options?: { sidebar?: boolean }) => {
      sendIframeMessage(IFrameMessageType.CmdOpenSession, { uuid: sessionUUID, sidebar: options?.sidebar || false })
    },
    openArtifact: (artifactUUID: string, options?: { target?: 'sidebar' | 'modal' }) => {
      sendIframeMessage(IFrameMessageType.CmdOpenArtifact, { uuid: artifactUUID, target: options?.target })
    },
    navigate: (url: string) => {
      sendIframeMessage(IFrameMessageType.CmdNavigate, { url })
    },
    onRouteChanged: (internalRoute: string, navId?: number) => {
      sendIframeMessage(IFrameMessageType.CmdRouteChanged, { internalRoute, navId })
    },
    handleError: (code: 'auth-expired' | 'other', error: Record<string, unknown>) => {
      sendIframeMessage(IFrameMessageType.CmdHandleError, { error, code })
    },
    confirm: (options: LimitedConfirmationOptions) => {
      const newUuid = uuid()
      sendIframeMessage(IFrameMessageType.CmdShowConfirm, { ...options, uuid: newUuid })
      return new Promise<boolean>((resolve, reject) => {
        const messageHandler = (event: MessageEvent) => {
          const dataNormalized = JSON.parse(event.data)
          if (dataNormalized.type === IFRAME_MESSAGE_TYPE && dataNormalized.action === IFrameMessageType.OnConfirmResult && dataNormalized.uuid === newUuid) {
            window.removeEventListener('message', messageHandler)
            if (typeof dataNormalized.result !== 'undefined') {
              resolve(!!dataNormalized.result)
            }
            else {
              reject(new Error('Confirmation result is undefined'))
            }
          }
        }
        window.addEventListener('message', messageHandler)
      })
    },
    toast: (message: ToastMessageOptions) => {
      sendIframeMessage(IFrameMessageType.CmdShowToast, { ...message })
    },
    formatUrl: (relativeUrl: string) => {
      return `${appConfig.routePrefix || ''}${relativeUrl}`
    },
    classifyLink: (href: string | null | undefined): LinkClassification => {
      return classifyLink(href, {
        mountRoutes: appConfig.mountRoutes,
        routePrefix: appConfig.routePrefix,
        selfPageId: appConfig.selfPageId,
        hostOrigin: typeof window !== 'undefined' && window.location?.ancestorOrigins?.length
          ? window.location.ancestorOrigins[0]
          : undefined,
      })
    },
    setContext: (
      context: Record<string, unknown>,
      sessionUUID?: string,
      source?: { type: 'page' | 'artifact', uuid: string },
    ) => {
      sendIframeMessage(IFrameMessageType.CmdSetContext, { context, sessionUUID, source: source || null })
    },
    layout: createLayoutAPI((appConfig.context?.layout as LayoutSnapshot | null | undefined) ?? null),
    bridge,
    logout: () => {
      sendIframeMessage(IFrameMessageType.CmdLogout, { })
    },
    setThemeMode: (mode: ThemeMode) => {
      sendIframeMessage(IFrameMessageType.CmdSetThemeMode, { mode })
    },
    getThemeMode: (): ThemeMode => getAppConfig().themeMode ?? 'auto',
  }
}

export function createIframeAPI(hostApi: ReturnType<typeof createHostAPI>) {
  return {
    startChat: (start_token: string, sidebar: boolean = false) => {
      console.warn('iframe.startChat is deprecated, use new host.startChat instead')
      hostApi.startChat(start_token, { sidebar })
    },
    openSession: (sessionUUID: string, sidebar: boolean = false) => {
      console.warn('iframe.openSession is deprecated, use new host.openSession instead')
      hostApi.openSession(sessionUUID, { sidebar })
    },
    openArtifact: (artifactUUID: string, target?: 'sidebar' | 'modal') => {
      console.warn('iframe.openArtifact is deprecated, use new host.openArtifact instead')
      hostApi.openArtifact(artifactUUID, target ? { target } : undefined)
    },
    navigate: (url: string) => {
      console.warn('iframe.navigate is deprecated, use new host.navigate instead')
      hostApi.navigate(url)
    },
    handleError: (code: 'auth-expired' | 'other', error: Record<string, unknown>) => {
      console.warn('iframe.handleError is deprecated, use new host.handleError instead')
      hostApi.handleError(code, error)
    },
    confirm: (options: LimitedConfirmationOptions) => {
      console.warn('iframe.confirm is deprecated, use new host.confirm instead')
      return hostApi.confirm(options)
    },
    toast: (message: ToastMessageOptions) => {
      console.warn('iframe.toast is deprecated, use new host.toast instead')
      hostApi.toast(message)
    },
    formatUrl: (relativeUrl: string) => {
      console.warn('iframe.formatUrl is deprecated, use new host.formatUrl instead')
      return hostApi.formatUrl(relativeUrl)
    },
    setContext: (
      context: Record<string, unknown>,
      source?: { type: 'page' | 'artifact', uuid: string },
    ) => {
      console.warn('iframe.setContext is deprecated, use new host.setContext instead')
      hostApi.setContext(context, undefined, source)
    },
    logout: () => {
      console.warn('iframe.logout is deprecated, use new host.logout instead')
      hostApi.logout()
    },
  }
}
